import { createFileRoute, Link } from "@tanstack/react-router";
import { PHYSICIAN_REF } from "@/lib/medical-schema";
import { SOCIAL_PREVIEW_IMAGE, SOCIAL_PREVIEW_ALT } from "@/lib/social-meta";
import prosperQrAsset from "@/assets/prosper-qr-code.png.asset.json";
import { Flower } from "@/components/site/Flower";
import { PageSection } from "@/components/site/PageSection";
import { ProcedureFaq } from "@/components/site/ProcedureFaq";
import { faqSchema, type Faq } from "@/components/site/InsuranceProse";
import { canonicalAndAlternates } from "@/lib/hreflang";
import { schemaDateFields } from "@/lib/schema-dates";

const PAGE_URL = "https://virginiabariatric.com/resources/prosper";
const PAGE_TITLE = "Weight-Loss Surgery Financing & Payment Plans | Virginia Bariatric";
const PAGE_DESCRIPTION =
  "How to pay for bariatric surgery in Northern Virginia: medical financing, payment plans, HSA/FSA, and what to do if you are turned down. Real numbers from Dr. Fitzer's practice.";

const FAQS: Faq[] = [
  {
    q: "Can I finance the entire cost, including the hospital?",
    a: "Usually yes — Prosper lends up to $50,000, which covers any of our procedures in full. You would then pay the hospital and anesthesia group from those funds.",
  },
  {
    q: "Does checking my rate hurt my credit?",
    a: "No. Prosper's rate check is a soft inquiry. A hard inquiry only happens if you accept an offer and proceed.",
  },
  {
    q: "Can I use insurance and financing together?",
    a: "Yes, and many patients do — insurance covers the procedure and financing covers the deductible, coinsurance, and anything excluded.",
  },
  {
    q: "What if I am declined?",
    a: "See the section above. Appeal any insurance denial, ask us about the endoscopic options, and consider reapplying after a few months.",
  },
  {
    q: "Do you offer in-house financing?",
    a: "Not as a loan. What we offer is the two-payment split on the surgeon fee described above, which is interest-free but short-term.",
  },
  {
    q: "Is any of this refundable if I change my mind?",
    a: "Payments toward the surgeon fee follow our cancellation policy: no fee if you cancel more than fourteen days before surgery, $500 inside fourteen days. Diet visits already completed are billed at $85 each.",
  },
];

export const Route = createFileRoute("/resources/prosper")({
  head: () => ({
    meta: [
      { title: PAGE_TITLE },
      { name: "description", content: PAGE_DESCRIPTION },
      ...(import.meta.env.SSR ? [
        { property: "og:title", content: PAGE_TITLE },
        { property: "og:description", content: PAGE_DESCRIPTION },
        { property: "og:type", content: "website" },
        { property: "og:url", content: PAGE_URL },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: PAGE_TITLE },
        { name: "twitter:description", content: PAGE_DESCRIPTION },
        { property: "og:image", content: SOCIAL_PREVIEW_IMAGE },
        { property: "og:image:alt", content: SOCIAL_PREVIEW_ALT },
        { name: "twitter:image", content: SOCIAL_PREVIEW_IMAGE },
        { name: "twitter:image:alt", content: SOCIAL_PREVIEW_ALT },
      ] : []),
    ],
    links: canonicalAndAlternates("/resources/prosper"),
    scripts: import.meta.env.SSR ? [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "MedicalWebPage",
          ...schemaDateFields("/resources/prosper"),
          reviewedBy: PHYSICIAN_REF,
          "@id": `${PAGE_URL}#webpage`,
          url: PAGE_URL,
          name: PAGE_TITLE,
          description: PAGE_DESCRIPTION,
          inLanguage: "en-US",
          isPartOf: { "@id": "https://virginiabariatric.com/#website" },
          about: { "@type": "MedicalCondition", name: "Obesity" },
          audience: {
            "@type": "MedicalAudience",
            audienceType: "Self-pay bariatric surgery patients",
          },
          specialty: "Bariatric",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify(faqSchema(FAQS, PAGE_URL)),
      },
    ] : [],
  }),
  component: ProsperPage,
});

function ProsperPage() {
  return (
    <>
      <PageSection className="relative pt-4 md:pt-6 pb-3 md:pb-4">
        <Flower className="hidden md:block right-2 top-6 w-56 lg:w-64" />
        <div className="text-center max-w-4xl mx-auto">
          <h1 className="text-4xl md:text-5xl font-serif">
            Paying for Weight-Loss Surgery: Financing and Payment Options
          </h1>
        </div>
      </PageSection>

      <PageSection className="pt-2 pb-8" maxWidth="4xl">
        <article className="text-foreground/80 leading-[1.6] text-base md:text-lg">
          <div className="space-y-4">
            <p>
              You&apos;ve spent a year or two mulling it over. You&apos;ve hemmed. You&apos;ve hawed. You hemmed some more. But finally, at long last, you&apos;ve made your decision: It&apos;s time! You&apos;re over watching other people get weight-loss surgery and enjoy lifestyle benefits you want for yourself. You&apos;re ready.
            </p>
            <p>
              Only, there&apos;s still one thing to figure out: how will you pay for it? Maybe you don&apos;t have bariatric coverage. Maybe you do, but you have a large deductible. Either way, you have a dilemma.
            </p>
            <p>
              There are options, and maybe ones you haven&apos;t thought of. Some patients combine two or three to make it work. What follows is a discussion of the financing options.
            </p>
          </div>

          <h2 className="mt-8 text-center font-serif text-2xl md:text-3xl text-brand-deep">
            What does the procedure cost?
          </h2>
          <div className="mt-3 space-y-4">
            <p>
              Obviously, it&apos;s helpful to know what you are financing. Thankfully, you can. Our{" "}
              <Link to="/self-pay-bariatric-surgery" className="text-brand-mid underline">
                self-pay prices
              </Link>{" "}
              are published on this site: $19,200 for a gastric sleeve, $23,200 for a gastric bypass, $12,900 for ESG, $8,900 for the Orbera balloon, and so on. For a full accounting of what is covered by the price, please visit our self-pay page.
            </p>
            <p>
              If you have insurance, your final cost is usually much less. Our office will{" "}
              <Link to="/resources/insurance-coverage" className="text-brand-mid underline">
                check your benefit
              </Link>{" "}
              and help come up with an estimate before you commit to anything.
            </p>
          </div>

          <h2 className="mt-8 text-center font-serif text-2xl md:text-3xl text-brand-deep">
            Four ways to pay
          </h2>
          <div className="mt-4 overflow-x-auto rounded-lg border border-border">
            <table className="w-full min-w-[42rem] border-collapse text-left text-sm md:text-base">
              <thead className="bg-brand-soft/60 text-foreground">
                <tr>
                  <th scope="col" className="p-3 font-semibold">Route</th>
                  <th scope="col" className="p-3 font-semibold">What it is</th>
                  <th scope="col" className="p-3 font-semibold">Best when</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                <tr>
                  <th scope="row" className="p-3 align-top font-semibold text-foreground">Insurance</th>
                  <td className="p-3 align-top">Your plan covers the procedure, you pay deductible and coinsurance</td>
                  <td className="p-3 align-top">Your plan includes bariatric benefits and you can meet the supervised-diet requirement</td>
                </tr>
                <tr>
                  <th scope="row" className="p-3 align-top font-semibold text-foreground">Self-pay</th>
                  <td className="p-3 align-top">You pay the published price directly, in one or two installments</td>
                  <td className="p-3 align-top">Your plan excludes surgery, or your deductible is high enough that coverage saves little</td>
                </tr>
                <tr>
                  <th scope="row" className="p-3 align-top font-semibold text-foreground">Medical financing</th>
                  <td className="p-3 align-top">A lender pays us, you repay the lender monthly</td>
                  <td className="p-3 align-top">You want to start now and spread the cost over two to six years</td>
                </tr>
                <tr>
                  <th scope="row" className="p-3 align-top font-semibold text-foreground">Medical HSA</th>
                  <td className="p-3 align-top">Your employers HSA can be applied to part or all of your surgical expense</td>
                  <td className="p-3 align-top">You have one! Not everybody does, but surgery is almost always a covered expense.</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p className="mt-4">
            Most self-pay patients use some combination — HSA money toward the hospital portion, financing for the surgeon fee, and so on. You do not have to pick one.
          </p>

          <h2 className="mt-8 text-center font-serif text-2xl md:text-3xl text-brand-deep">
            Loans through Prosper Healthcare Lending
          </h2>
          <p className="mt-3">
            We partner with Prosper® Healthcare Lending because their process is fast and because they specialize in this. The essentials:
          </p>
          <ul className="mt-3 list-disc space-y-2 pl-6">
            <li>Loans from $2,000 to $50,000, terms of two to six years</li>
            <li>APR range 8.99% to 35.99%, depending on credit</li>
            <li>Checking your rate does not affect your credit score</li>
            <li>You usually get a decision in minutes</li>
          </ul>
          <p>
            Patients who use Prosper apply directly, and you can do so now by scanning the QR code below. We never see your financial information, we do not approve or deny anything. Also, we are not paid any more if you use them. If you are approved, Prosper pays the surgeon fee, and you repay them.
          </p>

          <p className="text-center mt-4 font-bold">
            Click or scan the QR icon to start the application process.{" "}
            <span className="inline-block">
              Note: Applying to Prosper<sup className="text-[0.7em]">®</sup> does not adversely impact your credit.
            </span>
          </p>

          <div className="mt-6 text-center">
            <a
              href="https://www.prosper.com/personal-loans/healthcare-financing"
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-block rounded-2xl border border-border bg-card p-4 transition hover:-translate-y-1 hover:border-brand hover:shadow-xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/60 focus-visible:ring-offset-2"
            >
              <img
                src={prosperQrAsset.url}
                alt="Prosper® Healthcare Lending QR code linking to their patient financing application"
                width={1920}
                height={1920}
                loading="lazy"
                decoding="async"
                className="w-24 h-24 object-contain transition group-hover:opacity-90"
              />
            </a>
          </div>

          <p className="mt-4">
            One thing to keep in mind: the APR range can be wide. If your credit is strong, you may find better terms elsewhere.
          </p>

          <h2 className="mt-8 text-center font-serif text-2xl md:text-3xl text-brand-deep">
            Other lenders to think about
          </h2>
          <ul className="mt-3 list-disc space-y-3 pl-6">
            <li><strong>CareCredit.</strong> A health-care credit card accepted by many practices, usually offering promotional periods with no interest if the balance is cleared in time. Read the deferred-interest terms carefully: if any balance remains when the promotional period ends, interest is typically charged retroactively from the original date. That can come as a surprise.</li>
            <li><strong>Your own bank or credit union.</strong> Often the cheapest money available, particularly if you are an established customer. Credit unions in particular are worth a call before you take a medical-specialty loan.</li>
            <li><strong>A home equity line of credit.</strong> Usually the lowest rate of anything here, though it puts your house behind the debt.</li>
            <li><strong>A 0% APR credit card offer.</strong> Workable for the smaller endoscopic procedures if you can clear it inside the promotional window. Maybe less great for a $19,000 surgery.</li>
          </ul>
          <p className="mt-4">
            Compare the total you will repay, not the monthly payment. A longer term always looks cheaper per month and is usually more expensive overall.
          </p>

          <h2 className="mt-8 text-center font-serif text-2xl md:text-3xl text-brand-deep">
            HSA, FSA, and employer benefits
          </h2>
          <div className="mt-3 space-y-4">
            <p>
              Bariatric surgery is almost always a qualified medical expense, so pre-tax health savings dollars can go toward it. Confirm with your plan administrator and keep your receipts along with any letter of medical necessity we provide.
            </p>
            <p>
              Also worth ten minutes with your HR department: some employers offer weight-management or surgical benefits that sit entirely outside the standard health plan. Patients are frequently surprised. Do not assume you have no coverage because your medical plan excludes surgery.
            </p>
          </div>

          <h2 id="if-you-cant-get-approved" className="mt-8 scroll-mt-24 text-center font-serif text-2xl md:text-3xl text-brand-deep">
            If all else fails...
          </h2>
          <p className="mt-3">
            Sometimes the answer from a lender is no. That doesn&apos;t necessarily have to be the end of it.
          </p>
          <div className="mt-4 space-y-4">
            <p>
              <strong>
                <Link to="/resources/insurance-coverage" className="text-brand-mid underline">
                  Appeal the insurance denial first.
                </Link>
              </strong>{" "}
              A surprising number of denials are procedural rather than substantive — a missing document, a miscoded diagnosis, a supervised-diet log that was one visit short. Our office does this regularly and we are stubborn about it.
            </p>
            <p><strong>Ask about hospital financial assistance.</strong> Nonprofit hospitals maintain financial assistance policies. Be aware that these generally apply to medically necessary care rather than elective self-pay surgery, so this is a long shot — but it costs nothing to ask INOVA directly, and the answer depends on your circumstances.</p>
            <p><strong>Consider the endoscopic options.</strong> If the surgical price is out of reach, ESG at $12,900 or the Orbera balloon at $8,900 are real procedures with real results, not consolation prizes. They are also easier to finance because the amounts are smaller. Whether they suit you is a clinical question — Dr. Fitzer will tell you honestly at your consultation.</p>
            <p><strong>Rebuild and come back.</strong> A declined application is usually about debt-to-income ratio or a recent credit event, and both change. Patients who were turned down are often approved six or twelve months later.</p>
            <p><strong>On crowdfunding.</strong> It&apos;s been done. Patients do try to raise money this way, and some succeed. Make a cute pitch video, and who knows?</p>
          </div>

          <ProcedureFaq heading="Frequently asked questions" faqs={FAQS} />

          <h2 className="mt-8 text-center font-serif text-2xl md:text-3xl text-brand-deep">
            The next step
          </h2>
          <div className="mt-3 space-y-4">
            <p>
              Call <a href="tel:7037099771" className="text-brand-mid underline">(703) 709-9771</a> and ask for a written quote. Our office will confirm your self-pay total or verify your insurance benefit, walk you through the payment schedule, and answer financing questions without any obligation to book.
            </p>
            <p>
              Or{" "}
              <Link to="/appointments" className="text-brand-mid underline">
                request a consultation
              </Link>{" "}
              and we will sort the money out at the same visit.
            </p>
          </div>

          <p className="mt-8 border-t border-border pt-5 text-sm italic text-muted-foreground">
            Medical and financial disclaimer: The information on this page is general and is not financial advice. Loan terms, rates, and promotional offers are set by the lender, change frequently, and depend on your credit. Confirm all terms directly with the lender before you borrow. Pricing shown is an estimate of self-pay charges and is subject to change.
          </p>
        </article>
      </PageSection>
    </>
  );
}

